import { Injectable } from '@angular/core';
import {
  HttpClient,
  HttpHeaders,
  HttpErrorResponse,
} from '@angular/common/http';

import { Observable, throwError } from 'rxjs';

import { catchError, tap, retryWhen, delay, take } from 'rxjs/operators';
import { ConfigService } from './config.service';
import {
  Referral,
  RecaptchaResponse,
  RecaptchaVerification,
  BoomiCallback,
  Source,
  ApplicationAccess,
  ApplicationConfig,
  Address,
  Application
} from '../../shared/models';

@Injectable({
  providedIn: 'root',
})
export class ReferralApiService {
  httpOptions: any;
  apiUrl: string;
  token: string;

  constructor(private config: ConfigService, private http: HttpClient) {
    this.httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
    };

    this.apiUrl = this.config.referralsApiUrl;
  }

  handleError(error: HttpErrorResponse) {
    console.warn('Something went wrong in api service');
    return throwError(error);
  }

  getFullUrl(pathSegment: string): string {
    return `${this.config.referralsApiUrl}${pathSegment}`;
  }

  getLookups() {
    const url = this.getFullUrl('/lookups');

    return this.http.get(url).pipe(catchError(this.handleError));
  }

  getLookupSource(name: string): Observable<Source> {
    const url = this.getFullUrl(`/lookups/source/${name}`);

    return this.http.get<Source>(url).pipe(catchError(this.handleError));
  }

  getLookupAccess(
    sourceId: number,
    applicationId: number
  ): Observable<ApplicationAccess> {
    const url = this.getFullUrl(
      `/lookups/applications/${applicationId}/access?sourceId=${sourceId}`
    );

    return this.http
      .get<ApplicationAccess>(url)
      .pipe(catchError(this.handleError));
  }

  getApplicationLookups(): Observable<Application[]> {
    const url = this.getFullUrl('/lookups/applications');

    return this.http.get<Application[]>(url).pipe(catchError(this.handleError));
  }

  getApplicationSourceConfig(sourceId: number) {
    const url = this.getFullUrl(
      `/lookups/applications/1/config?sourceId=${sourceId}`
    );

    return this.http.get(url).pipe(catchError(this.handleError));
  }

  getLookupApplicationSourceConfigByName(
    sourceName: string
  ): Observable<ApplicationConfig[]> {
    const url = this.getFullUrl(
      `/lookups/applications/1/${sourceName}/config`
    );

    return this.http.get<ApplicationConfig[]>(url).pipe(catchError(this.handleError));
  }

  getCallbackAvailability(department: string) {
    const url = this.getFullUrl(`/boomicallbacks/availability/${department}`);

    return this.http.get(url).pipe(catchError(this.handleError));
  }

  addBoomiCallback(callback: BoomiCallback) {
    const url = this.getFullUrl('/boomicallbacks');

    return this.http
      .post<BoomiCallback>(url, callback, {
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
          Authorization: `Bearer ${this.token}`,
        }),
      })
      .pipe(catchError(this.handleError));
  }

  getBoomiCallback(callbackId: string) {
    const url = this.getFullUrl(`/boomicallbacks/${callbackId}`);

    return this.http.get(url).pipe(catchError(this.handleError));
  }

  cancelBoomiCallback(callbackId: string) {
    const url = this.getFullUrl('/boomicallbacks');

    return this.http
      .delete(`${url}/${callbackId}`)
      .pipe(catchError(this.handleError));
  }

  addReferral(referral: Referral) {
    const url = this.getFullUrl('/referrals');

    return this.http
      .post<Referral>(url, referral, {
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
          Authorization: `Bearer ${this.token}`,
        }),
      })
      .pipe(catchError(this.handleError));
  }

  sendRecaptcha(response: RecaptchaResponse) {
    const url = this.getFullUrl('/recaptcha');

    return this.http
      .post<RecaptchaVerification>(url, response, {
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
        }),
      })
      .pipe(catchError(this.handleError));
  }

  trackLink(trackingId: string) {
    const url = this.getFullUrl(`/communications/${trackingId}/track`);

    return this.http
      .post<any>(url, null, {
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
        }),
      })
      .pipe(catchError(this.handleError));
  }

  checkEmailDomain(email: string) {
    const url = this.getFullUrl('/referrals/validateemail');

    return this.http
      .post<any>(url + `?email=${email}`, null, {
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
        }),
      })
      .pipe(catchError(this.handleError));
  }

  getAddresses(postcode: string): Observable<Address[]> {
    const url = this.getFullUrl(`/lookups/postcode-search?source=Referral&postcode=${postcode}`);

    return this.http.get<Address[]>(url).pipe(catchError(this.handleError));
  }
}
